import { Box } from '@mui/joy';
import { Receipt } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DefaultPricingFormField } from 'contacts/components/AddContactGroupByEmail/Steps/ContactGroupDetails/AddContactGroup/DefaultPricingFormField';
import { DefaultPricingType } from 'contacts/enums';
import { useContactGroupActions } from 'contacts/hooks';
import { IContactGroup, IUpdateContactGroupDefaultPricingPayload } from 'contacts/types';
import { Button, Form, SlidingPanelContent, SubtitleCard } from 'core/components';
import { FormProvider, useNotificationContext } from 'core/states';
import { getPercentage } from 'core/utils/number';
import { DiscountPayloadType } from 'corporate/enums';
import { useGetDiscounts } from 'corporate/hooks';
import { IDiscount } from 'corporate/types';
import { PricingProfileList } from 'pricing/components';
import { PricingProfilePricingType, PricingProfileType } from 'pricing/enums';
import { useGetPricingProfiles } from 'pricing/hooks';
import { IPricingProfile } from 'pricing/types';
import { Variant } from 'theme/enums';

import { defaultPricingSchema } from './schema';

interface IProps {
  contactGroup: IContactGroup;
  onSubmitSuccess?: (contactGroup: IContactGroup) => void;
}

interface IDefaultPricingForm {
  defaultPricing: DefaultPricingType;
  baseMargin: number | null;
  baseDiscount: number | null;
}

export const ContactGroupPricing = ({ contactGroup, onSubmitSuccess }: IProps) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [discountStructureId, setDiscountStructureId] = useState<number | null>(null);
  const [marginStructureId, setMarginStructureId] = useState<number | null>(null);
  const [defaultPricingProfileId, setDefaultPricingProfileId] = useState<number | null>(null);
  const [discountId, setDiscountId] = useState<number | null>(null);
  const [marginId, setMarginId] = useState<number | null>(null);

  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();
  const { updateContactGroupDefaultPricing } = useContactGroupActions();

  const onGetPricingProfilesSuccess = (data: { records: IPricingProfile[]; count: number }) => {
    if (data.records?.[0]) {
      const profile = data.records[0];
      setMarginStructureId(profile.marginStructureId || null);
      setDiscountStructureId(profile.discountStructureId || null);
      setDefaultPricingProfileId(profile.id || null);
    }
  };

  const onGetDiscountsMarginSuccess = (data: { records: IDiscount[]; count: number }) => {
    if (data.records?.[0]) {
      setMarginId(data.records[0].id || null);
    }
  };

  const onGetDiscountsDiscountSuccess = (data: { records: IDiscount[]; count: number }) => {
    if (data.records?.[0]) {
      setDiscountId(data.records[0].id || null);
    }
  };

  useGetPricingProfiles(
    {
      filters: { type: PricingProfileType.BASE },
      viewBy: { contactGroup: contactGroup.id },
    },
    !discountStructureId && !marginStructureId,
    onGetPricingProfilesSuccess
  );

  useGetDiscounts(
    {
      filters: {
        pricingProfileId: defaultPricingProfileId,
        structureId: marginStructureId,
      },
    },
    !!defaultPricingProfileId && !!marginStructureId,
    onGetDiscountsMarginSuccess
  );

  useGetDiscounts(
    {
      filters: {
        pricingProfileId: defaultPricingProfileId,
        structureId: discountStructureId,
      },
    },
    !!defaultPricingProfileId && !!discountStructureId,
    onGetDiscountsDiscountSuccess
  );

  const defaultPricingValue = contactGroup.baseMargin
    ? t('labelBaseMarginWithValue', {
        value: getPercentage(contactGroup.baseMargin, {
          isRawPercentage: true,
        }),
      })
    : contactGroup.baseDiscount
    ? t('labelBaseDiscountWithValue', {
        value: getPercentage(contactGroup.baseDiscount, {
          isRawPercentage: true,
        }),
      })
    : t('labelNoDefaultPricingSet');

  const formDefaultValues = useMemo(() => {
    return {
      defaultPricing: contactGroup.baseMargin
        ? DefaultPricingType.BASE_MARGIN
        : contactGroup.baseDiscount
        ? DefaultPricingType.BASE_DISCOUNT
        : DefaultPricingType.BASE_MARGIN,
      baseMargin: contactGroup.baseMargin || null,
      baseDiscount: contactGroup.baseDiscount || null,
    };
  }, [contactGroup.baseMargin, contactGroup.baseDiscount]);

  const onEditClick = () => {
    setIsEditOpen(true);
  };

  const onClose = () => {
    clearNotification();
    setMarginStructureId(null);
    setDiscountStructureId(null);
    setDefaultPricingProfileId(null);
    setMarginId(null);
    setDiscountId(null);
    setIsEditOpen(false);
  };

  const handleOnClose = (reset?: UseFormReset<IDefaultPricingForm>) => {
    if (reset) {
      reset(formDefaultValues);
    }
    onClose();
  };

  const onSubmit = () => async (data: IDefaultPricingForm) => {
    const updateData: IUpdateContactGroupDefaultPricingPayload = {
      structureId:
        data.defaultPricing === DefaultPricingType.BASE_MARGIN
          ? marginStructureId
          : discountStructureId,
      discount:
        data.defaultPricing === DefaultPricingType.BASE_MARGIN
          ? data.baseMargin
          : data.baseDiscount,
      type:
        data.defaultPricing === DefaultPricingType.BASE_MARGIN
          ? DiscountPayloadType.MARGIN
          : DiscountPayloadType.DISCOUNT,
      id: data.defaultPricing === DefaultPricingType.BASE_MARGIN ? marginId : discountId,
    };

    const { success, discount } = await updateContactGroupDefaultPricing(updateData);

    if (!success) {
      return;
    }

    if (onSubmitSuccess) {
      const updatedContactGroup: IContactGroup = {
        ...contactGroup,
        baseMargin: discount.type === DiscountPayloadType.MARGIN ? discount.discount : null,
        baseDiscount: discount.type === DiscountPayloadType.DISCOUNT ? discount.discount : null,
      };
      onSubmitSuccess(updatedContactGroup);

      setTimeout(() => {
        onClose();
      }, 1000);
    }
  };

  return (
    <>
      <SlidingPanelContent title={t('labelPricing')}>
        <Box sx={{ mb: 2, maxWidth: 350 }}>
          <SubtitleCard
            label={t('labelDefaultPricing')}
            value={defaultPricingValue}
            icon={<Receipt weight="fill" />}
            buttons={[
              <Button
                key="editPricing"
                text={t('buttonLabelEdit')}
                variant={Variant.SOFT}
                onClick={onEditClick}
                disabled={!contactGroup.baseMargin && !contactGroup.baseDiscount}
              />,
            ]}
          />
        </Box>
        <PricingProfileList
          type={PricingProfilePricingType.PRICING}
          contactGroupId={contactGroup.id}
        />
      </SlidingPanelContent>

      <FormProvider
        schema={defaultPricingSchema()}
        defaultValues={formDefaultValues}
        resetDefaultValues
      >
        <Form
          title={t('titleEditDefaultPricing')}
          isOpen={isEditOpen}
          onClose={handleOnClose}
          onSubmit={onSubmit}
          submitBtnLabel={t('buttonLabelSaveChanges')}
          cancelBtnLabel={t('buttonLabelDiscardChanges')}
          interactive
        >
          <DefaultPricingFormField />
        </Form>
      </FormProvider>
    </>
  );
};
